import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import type { UserProfile } from '@regatta-replay/types';
import {
  AuthShell,
  AuthError,
  authFieldClass,
  authLabelClass,
  authPrimaryButtonClass,
  authGhostButtonClass,
} from '@/components/auth/AuthShell';

/**
 * Signup onboarding — claim your boat.
 *
 * Runs immediately after signup (direction A: claim inside signup) so the
 * account ends up attached to a boat while intent is highest. Every step can
 * be skipped, and the same claim can be changed later from /profile.
 */

type Step = 'regatta' | 'boat' | 'confirm' | 'claimed' | 'prospect' | 'unmatched';

const SUPPORT_EMAIL = 'hello@regattareplay.com';

type BoatRole = 'skipper' | 'owner' | 'co-owner' | 'crew' | 'supporter';
const BOAT_ROLES: BoatRole[] = ['skipper', 'owner', 'co-owner', 'crew', 'supporter'];
const CREW_POSITIONS = ['tactician', 'bowman', 'trimmer', 'pit'];

/** Supporters didn't sail — a partner, friend, or fan following the boat. */
const ROLE_LABELS: Record<BoatRole, string> = {
  skipper: 'Skipper',
  owner: 'Owner',
  'co-owner': 'Co-owner',
  crew: 'Crew',
  supporter: 'Supporter',
};

interface RegattaSummary {
  regattaId: string;
  name: string;
  venue?: string;
  startDate?: string;
  seasonYear?: number;
}

interface ClaimableEntry {
  entryId: string;
  boatId?: string;
  sailNumber: string;
  boatName: string;
  skipperName: string;
  claimedBy?: string;
  claimedRole?: string;
}

interface ClaimResponse {
  success: true;
  user: UserProfile;
  token: string;
  boat: { boatId: string; sailNumber: string; boatName: string; role: string; crewPosition?: string };
}

function StepDots({ index }: { index: 0 | 1 | 2 }) {
  return (
    <div className="mb-5 flex items-center justify-center gap-1.5">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={`h-[3px] w-7 rounded-full ${
            i < index ? 'bg-viewer-accentDeep' : i === index ? 'bg-viewer-accent' : 'bg-viewer-line2/25'
          }`}
        />
      ))}
    </div>
  );
}

function OptionRow({
  onClick,
  selected,
  title,
  meta,
  right,
}: {
  onClick: () => void;
  selected?: boolean;
  title: string;
  meta?: string;
  right?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`mb-2 flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${
        selected
          ? 'border-viewer-accent bg-viewer-accent/10'
          : 'border-viewer-line/25 bg-viewer-panel hover:border-viewer-line2/40'
      }`}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-semibold text-viewer-ink">{title}</span>
        {meta && <span className="mt-0.5 block truncate text-[11px] text-viewer-muted">{meta}</span>}
      </span>
      {right}
    </button>
  );
}

function Chip({
  label,
  selected,
  onClick,
  disabled,
  title,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
      className={`rounded-full border px-3 py-1.5 text-[12.5px] capitalize transition-colors ${
        disabled
          ? 'cursor-not-allowed border-viewer-line/25 text-viewer-faint line-through'
          : selected
            ? 'border-viewer-accent bg-viewer-accent/15 font-semibold text-viewer-ink'
            : 'border-viewer-line2/40 text-viewer-muted hover:text-viewer-ink'
      }`}
    >
      {label}
    </button>
  );
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  const { isAuthenticated, user, login, token: authToken } = useAuthStore();

  const [step, setStep] = useState<Step>('regatta');
  const [regattas, setRegattas] = useState<RegattaSummary[]>([]);
  const [regattaId, setRegattaId] = useState<string | null>(null);
  const [entries, setEntries] = useState<ClaimableEntry[]>([]);
  const [query, setQuery] = useState('');
  const [entry, setEntry] = useState<ClaimableEntry | null>(null);
  const [role, setRole] = useState<BoatRole>('skipper');
  const [crewPosition, setCrewPosition] = useState<string | null>(null);
  const [claimed, setClaimed] = useState<ClaimResponse['boat'] | null>(null);
  const [conflict, setConflict] = useState<{ claimedBy: string; claimedRole: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prospect branch
  const [sailingRole, setSailingRole] = useState<string | null>(null);
  const [boatClassInterest, setBoatClassInterest] = useState('');
  const [homeClubName, setHomeClubName] = useState('');

  useEffect(() => {
    api
      .get<{ items: RegattaSummary[] }>('/regattas')
      .then((res) => setRegattas(res.items ?? []))
      .catch(() => setRegattas([]));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) =>
      `${e.sailNumber} ${e.boatName} ${e.skipperName}`.toLowerCase().includes(q),
    );
  }, [entries, query]);

  function finish() {
    // Return them to what they were doing; otherwise to their boat's regatta.
    if (redirectTo) {
      window.location.href = redirectTo;
    } else if (regattaId) {
      navigate(`/regatta/${regattaId}`);
    } else {
      navigate('/');
    }
  }

  async function chooseRegatta(id: string) {
    setRegattaId(id);
    setLoading(true);
    setError(null);
    try {
      // `silent` for the same reason as submitClaim below: this page renders its
      // own error, and the global toast would double it with the raw API string.
      const res = await api.get<{ items: ClaimableEntry[] }>(`/me/claimable/${id}`, {
        silent: true,
      });
      setEntries(res.items ?? []);
      setStep('boat');
    } catch {
      setError('We couldn’t load that regatta’s boats. Try again, or pick a different regatta.');
    } finally {
      setLoading(false);
    }
  }

  async function submitClaim(chosenRole: BoatRole) {
    if (!entry || !regattaId) return;
    setLoading(true);
    setError(null);
    setConflict(null);

    try {
      // `silent: true` — EVERY outcome of this call is handled below, including
      // the expected ones. Without it the global handler in lib/api.ts fires a
      // red "Request failed" toast carrying the raw body, and because the API
      // answers a refused claim with `{ error: 'UNMATCHED' }` (a CODE, not
      // prose) that toast read literally "Request failed — UNMATCHED": an
      // internal string with no way forward, on top of the recovery screen we
      // were already showing. A refused claim is a normal answer, not a fault.
      const res = await api.post<ClaimResponse>(
        '/me/claim-entry',
        {
          regattaId,
          entryId: entry.entryId,
          role: chosenRole,
          ...(chosenRole === 'crew' && crewPosition ? { crewPosition } : {}),
        },
        { silent: true },
      );
      // The claim links the account to a sailor, so the session token is
      // reissued with the sailorId on it.
      login(res.token, res.user);
      setClaimed(res.boat);
      setStep('claimed');
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      // The API answers with the CODE as its error, so that is what reaches us
      // here — matching on the prose would break on a copy edit (and did).
      if (/UNMATCHED/i.test(message)) {
        // We won't invent a sailor record for an unproven claim — offer the
        // two honest ways forward instead.
        setStep('unmatched');
      } else if (/ROLE_TAKEN/i.test(message) && entry.claimedBy) {
        setConflict({ claimedBy: entry.claimedBy, claimedRole: entry.claimedRole ?? 'skipper' });
      } else {
        // Anything else — a 500, a dropped connection, a code we don't know yet.
        // The raw string is an internal detail that means nothing to a sailor
        // and offers no way forward, so it never reaches the screen: say what
        // happened, and name the two doors that are still open. Logged so the
        // detail is recoverable from the console.
        console.error('[onboarding] claim failed:', err);
        setError(
          'We couldn’t complete that claim. You can follow this boat instead, or email ' +
            `${SUPPORT_EMAIL} and we’ll link it for you.`,
        );
      }
    } finally {
      setLoading(false);
    }
  }

  async function submitProspect() {
    setLoading(true);
    setError(null);
    try {
      await api.post('/me/onboarding', {
        ...(sailingRole ? { sailingRole } : {}),
        ...(boatClassInterest.trim() ? { boatClassInterest: boatClassInterest.trim() } : {}),
        ...(homeClubName.trim() ? { homeClubName: homeClubName.trim() } : {}),
      });
      finish();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your answers');
      setLoading(false);
    }
  }

  if (!isAuthenticated || !authToken) {
    return (
      <AuthShell title="Sign in first" subtitle="Claiming a boat needs an account.">
        <Link to="/signup" className={authPrimaryButtonClass + ' block text-center'}>
          Create an account
        </Link>
      </AuthShell>
    );
  }

  // ── Step: which regatta ──
  if (step === 'regatta') {
    return (
      <AuthShell title="Find your boat" subtitle="Which regatta did you sail?" showLogo={false}>
        <StepDots index={0} />
        {regattas.map((r) => (
          <OptionRow
            key={r.regattaId}
            onClick={() => chooseRegatta(r.regattaId)}
            selected={regattaId === r.regattaId}
            title={r.name}
            meta={[r.venue, r.seasonYear].filter(Boolean).join(' · ')}
          />
        ))}

        <p className="mb-2 mt-5 font-viewer-display text-[11px] font-bold uppercase tracking-widest text-viewer-muted">
          Or
        </p>
        <OptionRow
          onClick={() => setStep('prospect')}
          title="I sailed a different regatta"
          meta="Tell us about your boat instead"
        />

        <AuthError message={error} />

        <button onClick={finish} className="mt-4 w-full text-center text-[12.5px] text-viewer-accent hover:underline">
          Skip for now
        </button>
      </AuthShell>
    );
  }

  // ── Step: pick the boat ──
  if (step === 'boat') {
    return (
      <AuthShell
        title="Find your boat"
        subtitle={`${entries.length} boats`}
        showLogo={false}
      >
        <StepDots index={1} />
        <input
          className={`${authFieldClass} mb-3`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search sail number, boat or skipper"
          autoFocus
        />

        <div className="max-h-[340px] overflow-y-auto pr-0.5">
          {filtered.map((e) => (
            <OptionRow
              key={e.entryId}
              onClick={() => {
                setEntry(e);
                setConflict(e.claimedBy ? { claimedBy: e.claimedBy, claimedRole: e.claimedRole ?? 'skipper' } : null);
                setRole(e.claimedBy ? 'crew' : 'skipper');
                setStep('confirm');
              }}
              title={`${e.sailNumber} · ${e.boatName}`}
              meta={e.skipperName}
              right={
                // Name the role that's taken, not "Claimed" — the boat itself
                // is always joinable, and a whole crew will claim this row.
                e.claimedBy ? (
                  <span className="shrink-0 text-[11px] capitalize text-viewer-muted">
                    {e.claimedRole ?? 'skipper'}: {e.claimedBy}
                  </span>
                ) : undefined
              }
            />
          ))}
          {filtered.length === 0 && (
            <p className="py-6 text-center text-[12.5px] text-viewer-faint">No boats match that.</p>
          )}
        </div>

        <AuthError message={error} />

        <button
          onClick={() => setStep('prospect')}
          className="mt-3 w-full text-center text-[12.5px] text-viewer-accent hover:underline"
        >
          My boat isn’t listed
        </button>
      </AuthShell>
    );
  }

  // ── Step: confirm + role ──
  if (step === 'confirm' && entry) {
    // Only a ROLE can be taken, never the boat. A whole crew claims the same
    // boat — that is the normal case, not a collision — so the exclusive roles
    // grey out and everything else stays open.
    const heldBy = conflict;
    const roleTaken = (r: BoatRole) =>
      !!heldBy && r !== 'crew' && r !== 'supporter';

    return (
      <AuthShell title="Is this you?" subtitle="Confirm and tell us your role." showLogo={false}>
        <StepDots index={2} />

        <div className="mb-4 rounded-xl border border-viewer-line2/40 bg-gradient-to-b from-viewer-panel2 to-viewer-panel p-4 text-center">
          <p className="font-viewer-mono text-[11px] tracking-wider text-viewer-accent">
            {entry.sailNumber}
          </p>
          <p className="mt-1 font-viewer-display text-[22px] font-bold text-viewer-ink">
            {entry.boatName}
          </p>
          {entry.skipperName && (
            <p className="mt-0.5 text-[12px] text-viewer-muted">Skipper: {entry.skipperName}</p>
          )}
          <button
            onClick={() => setStep('boat')}
            className="mt-2 text-[11.5px] text-viewer-accent hover:underline"
          >
            Choose a different boat
          </button>
        </div>

        <p className={authLabelClass}>How were you involved?</p>
        <div className="mb-2 flex flex-wrap gap-2">
          {BOAT_ROLES.map((r) => (
            <Chip
              key={r}
              label={ROLE_LABELS[r]}
              selected={role === r}
              disabled={roleTaken(r)}
              title={roleTaken(r) ? `Already claimed by ${heldBy!.claimedBy}` : undefined}
              onClick={() => setRole(r)}
            />
          ))}
        </div>

        {heldBy ? (
          <p className="mb-4 text-[11.5px] leading-snug text-viewer-muted">
            <b className="text-viewer-ink">{heldBy.claimedBy}</b> already claimed this boat as{' '}
            {heldBy.claimedRole}. You can still join as crew or follow it as a supporter. We only
            ever show a first name and last initial.
          </p>
        ) : (
          <p className="mb-4 text-[11px] text-viewer-faint">
            {role === 'supporter'
              ? 'You’ll follow this boat. We won’t add you to the race record.'
              : 'Pick Supporter if you didn’t sail but want to follow this boat.'}
          </p>
        )}

        {role === 'crew' && (
          <>
            <p className={authLabelClass}>
              Position <span className="font-normal text-viewer-faint">— optional</span>
            </p>
            <div className="mb-4 flex flex-wrap gap-2">
              {CREW_POSITIONS.map((pos) => (
                <Chip
                  key={pos}
                  label={pos}
                  selected={crewPosition === pos}
                  onClick={() => setCrewPosition(crewPosition === pos ? null : pos)}
                />
              ))}
            </div>
          </>
        )}

        <AuthError message={error} />

        {/* A failed claim must never be a dead end. The two doors that are still
            open — follow the boat, or ask a human — sit right under the error
            rather than being something the sailor has to go and find. */}
        {error && (
          <div
            data-testid="claim-recovery"
            className="mb-4 flex flex-wrap items-center gap-3 text-[12px]"
          >
            {role !== 'supporter' && (
              <button
                className="font-semibold text-viewer-accent hover:underline"
                onClick={() => {
                  setRole('supporter');
                  submitClaim('supporter');
                }}
                disabled={loading}
              >
                Follow this boat instead
              </button>
            )}
            <a
              className="text-viewer-muted hover:text-viewer-ink hover:underline"
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
                `Boat claim — ${entry.sailNumber} ${entry.boatName}`,
              )}`}
            >
              Email {SUPPORT_EMAIL}
            </a>
          </div>
        )}

        <button className={authPrimaryButtonClass} onClick={() => submitClaim(role)} disabled={loading}>
          {loading ? 'Claiming…' : role === 'supporter' ? 'Follow this boat' : 'Claim this boat'}
        </button>

        <button onClick={finish} className="mt-3 w-full text-center text-[12.5px] text-viewer-accent hover:underline">
          Skip for now
        </button>
      </AuthShell>
    );
  }


  // ── Step: we can't prove who you are ──
  if (step === 'unmatched' && entry) {
    const subject = `Boat claim — ${entry.sailNumber} ${entry.boatName}`;
    const body =
      `I signed up as ${user?.email ?? ''} and I'd like to claim ${entry.sailNumber} ` +
      `${entry.boatName}. The email on the entry is different.`;

    return (
      <AuthShell title="We can’t match you to this boat" showLogo={false}>
        <div className="mb-4 rounded-xl border border-viewer-line2/40 bg-gradient-to-b from-viewer-panel2 to-viewer-panel p-4 text-center">
          <p className="font-viewer-mono text-[11px] tracking-wider text-viewer-accent">
            {entry.sailNumber}
          </p>
          <p className="mt-1 font-viewer-display text-[22px] font-bold text-viewer-ink">
            {entry.boatName}
          </p>
        </div>

        <p className="mb-5 text-[13px] leading-relaxed text-viewer-muted">
          You signed up as <b className="text-viewer-ink">{user?.email}</b>, which isn’t the address
          on this entry — so we can’t confirm you sailed it. We won’t attach you to someone else’s
          race record on a guess.
        </p>

        <button className={authPrimaryButtonClass} onClick={() => submitClaim('supporter')} disabled={loading}>
          {loading ? 'Following…' : 'Follow this boat instead'}
        </button>
        <p className="mt-2 mb-4 text-[11.5px] text-viewer-faint">
          Supporters follow a boat without claiming a place on it — you’ll still get its races.
        </p>

        <a
          className={`${authGhostButtonClass} block text-center`}
          href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}
        >
          Email us to sort it out
        </a>
        <p className="mt-2 text-center text-[11.5px] text-viewer-faint">
          {SUPPORT_EMAIL} — tell us which boat is yours and we’ll link it.
        </p>

        <button onClick={() => setStep('boat')} className="mt-5 w-full text-center text-[12.5px] text-viewer-accent hover:underline">
          Choose a different boat
        </button>
      </AuthShell>
    );
  }

  // ── Step: claimed ──
  if (step === 'claimed' && claimed) {
    return (
      <AuthShell
        title={`${claimed.boatName} is yours`}
        subtitle={[claimed.sailNumber, claimed.role, claimed.crewPosition].filter(Boolean).join(' · ')}
        showLogo={false}
      >
        {!user?.emailVerifiedAt && (
          <div className="mb-4 rounded-lg border border-viewer-amber/30 bg-viewer-amber/10 px-3 py-2.5">
            <p className="text-[11.5px] leading-snug text-viewer-ink">
              Confirm your email to get race notifications. Check your inbox.
            </p>
          </div>
        )}

        <button onClick={finish} className={authPrimaryButtonClass}>
          See your races
        </button>
        <Link
          to="/profile"
          className="mt-4 block text-center text-[12.5px] font-semibold text-viewer-accent hover:underline"
        >
          Manage your boats
        </Link>
      </AuthShell>
    );
  }

  // ── Step: prospect branch ──
  return (
    <AuthShell
      title="Tell us about your sailing"
      subtitle="So we know which regattas to bring on next."
      showLogo={false}
    >
      <p className={authLabelClass}>Do you sail?</p>
      <div className="mb-4 flex flex-wrap gap-2">
        {[
          { key: 'owner', label: 'Own a boat' },
          { key: 'skipper', label: 'Skipper' },
          { key: 'crew', label: 'Crew' },
          { key: 'interested', label: 'Just interested' },
        ].map((o) => (
          <Chip
            key={o.key}
            label={o.label}
            selected={sailingRole === o.key}
            onClick={() => setSailingRole(sailingRole === o.key ? null : o.key)}
          />
        ))}
      </div>

      <div className="mb-3">
        <label htmlFor="ob-class" className={authLabelClass}>
          Boat class <span className="font-normal text-viewer-faint">— optional</span>
        </label>
        <input
          id="ob-class"
          className={authFieldClass}
          value={boatClassInterest}
          onChange={(e) => setBoatClassInterest(e.target.value)}
          placeholder="J/80, J/70, Melges 24…"
        />
      </div>

      <div className="mb-4">
        <label htmlFor="ob-club" className={authLabelClass}>
          Home club <span className="font-normal text-viewer-faint">— optional</span>
        </label>
        <input
          id="ob-club"
          className={authFieldClass}
          value={homeClubName}
          onChange={(e) => setHomeClubName(e.target.value)}
          placeholder="Royal Canadian Yacht Club"
        />
      </div>

      <AuthError message={error} />

      <button className={authPrimaryButtonClass} onClick={submitProspect} disabled={loading}>
        {loading ? 'Saving…' : 'Finish'}
      </button>
      <button onClick={finish} className="mt-3 w-full text-center text-[12.5px] text-viewer-accent hover:underline">
        Skip
      </button>
    </AuthShell>
  );
}
